/**
 * voice-v8 — `ablage_in_konfig`: was Karte (frontend/lib/karten/konfig-ablage.ts), Server (server/konfig-ablage.ts) und Werkzeug
 * (tools/konfig-ablage-werkzeug.ts) gemeinsam wissen. (28.10.2026, Autonomie-Modus B10 Teil b, Sicherheitsprüfung S3)
 *
 * ## Was es tut
 *
 * Zwei Zugangswerte, die schon in der Schlüsselablage liegen (memory/schluessel-ablage.ts, z. B. über B9), werden auf die FESTEN
 * Variablen des Postausgangs geschrieben: SES_SMTP_USER und SES_SMTP_PASS. HOST und PORT kommen NIE aus der Ablage — der HOST nur aus
 * der festen Liste der SES-Regionen, der PORT ist 587 (mit STARTTLS-Pflicht). Keine freien Variablennamen.
 *
 * ## Warum so eng (S3)
 *
 * Über diesen Zugang gehen die Anmelde-Mails (server/auth.ts `mailTransport`). Ein Host aus Fremdtext hieße: Anmeldelinks laufen über
 * einen fremden Server. Ein Zeilenumbruch oder „=“ in einem Wert hieße: beliebige Variablen in .env. Darum: Form je Wert geprüft,
 * HOST/PORT fest, Testmail an Martin VOR dem Schreiben, atomar, nur Martins eigener Knopf-Tipp. Wirksam erst nach einem Neustart —
 * der bleibt ein eigenes Ja.
 *
 * Notschalter: nur `ABLAGE_IN_KONFIG=1` schaltet ein (Vorgabe AUS).
 * Nur feste Wörter und Zeichen — nie ein Wert. Die Wortlaute beginnen nicht mit „Ja“ (kein Ja für ein anderes Tor).
 */

/** Notschalter: nur `ABLAGE_IN_KONFIG=1` schaltet ein (Vorgabe AUS). Je Aufruf gelesen. */
export function konfigAblageAn(env: Readonly<Record<string, string | undefined>> = process.env): boolean {
  return env.ABLAGE_IN_KONFIG?.trim() === "1";
}

/** Das Werkzeug — und der Name, unter dem seine Karte im Zug steht. */
export const KONFIG_KARTE = "ablage_in_konfig";

/** Die EINZIGEN Variablen, die dieser Weg schreibt — immer alle vier zusammen. */
export const KONFIG_ZIELE = ["SES_SMTP_USER", "SES_SMTP_PASS", "SES_SMTP_HOST", "SES_SMTP_PORT"] as const;
export type KonfigZiel = (typeof KONFIG_ZIELE)[number];

/** Der Port ist fest: 587 mit STARTTLS-Pflicht (`requireTLS`). */
export const KONFIG_PORT = 587;

/** Die SES-Regionen, deren SMTP-Rechnername erlaubt ist — feste Liste, nie freier Text. */
export const SES_REGIONEN = [
  "eu-central-1",
  "eu-west-1",
  "eu-west-2",
  "eu-west-3",
  "eu-north-1",
  "eu-south-1",
  "us-east-1",
  "us-east-2",
  "us-west-1",
  "us-west-2",
  "ca-central-1",
] as const;
export type SesRegion = (typeof SES_REGIONEN)[number];
export const REGION_VORGABE: SesRegion = "eu-central-1";

export function istSesRegion(v: unknown): v is SesRegion {
  return typeof v === "string" && (SES_REGIONEN as readonly string[]).includes(v);
}

/** Der Rechnername zur Region — der EINZIGE Weg zu einem HOST. */
export function sesHost(region: SesRegion): string {
  return `email-smtp.${region}.amazonaws.com`;
}

/** Form eines erlaubten HOST (für Karte und Prüfung). */
export const SES_HOST_FORM = /^email-smtp\.[a-z]{2}-[a-z]+-[0-9]\.amazonaws\.com$/;

/** SMTP-Benutzer von SES: eine Zugangsschlüssel-Kennung (AKIA + 16 Zeichen). */
export const SES_BENUTZER_FORM = /^AKIA[A-Z0-9]{16}$/;

/** SMTP-Passwort von SES: Base64-Zeichen ohne „=“, ohne Leerraum (heute 44 Zeichen). */
export const SES_PASSWORT_FORM = /^[A-Za-z0-9+/]{40,64}$/;

/** Kennung eines Vorschlags (6 Zufallsbytes als Hex) und Kurzwert (8 Hex-Zeichen eines HMAC). */
export const KONFIG_KENNUNG = /^[0-9a-f]{12}$/;
export const KONFIG_KURZWERT = /^[0-9a-f]{8}$/;

/** Ein Vorschlag gilt so lange ab der Karte — absolut, nie verlängert. */
export const KONFIG_FRIST_MS = 15 * 60_000;

/** Der feste Wortlaut des Schreiben-Knopfs — EINE Stelle für Karte und Prüfung. */
export function konfigKnopfJa(kennung: string, kurzwert: string): string {
  return `Postausgang schreiben (Konfig ${kennung}-${kurzwert})`;
}

/** Der Nicht-schreiben-Knopf derselben Karte. */
export function konfigKnopfNein(kennung: string): string {
  return `Postausgang nicht schreiben (Konfig ${kennung})`;
}

/** Sieht eine Nachricht aus wie einer der beiden Knöpfe (gleich welche Kennung)? */
export const KONFIG_KNOPF_FORM = /^Postausgang (?:schreiben \(Konfig [0-9a-f]{12}-[0-9a-f]{8}\)|nicht schreiben \(Konfig [0-9a-f]{12}\))$/;
